import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { PRES, SPACE, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState, SettingsPrompts } from '../presentationSettings';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminSettingCard from './AdminSettingCard';
import { adminFieldSx } from './adminFormSx';

interface AdminPromptsSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

type PromptKey = keyof SettingsPrompts;

const REWRITE_FIELDS: Array<{ key: PromptKey; label: string; help: string }> = [
  { key: 'rewriteSystem', label: COPY.settingsPromptRewriteSystem, help: COPY.settingsPromptRewriteSystemHelp },
  { key: 'rewriteUser', label: COPY.settingsPromptRewriteUser, help: COPY.settingsPromptRewriteUserHelp },
];

const OTHER_FIELDS: Array<{ key: PromptKey; label: string; help: string }> = [
  { key: 'grammarSystem', label: COPY.settingsPromptGrammar, help: COPY.settingsPromptGrammarHelp },
  { key: 'translateSystem', label: COPY.settingsPromptTranslate, help: COPY.settingsPromptTranslateHelp },
  { key: 'chatSystem', label: COPY.settingsPromptChat, help: COPY.settingsPromptChatHelp },
];

const AdminPromptsSection: React.FC<AdminPromptsSectionProps> = ({ draft, setDraft }) => {
  const prompts = draft.prompts;
  if (!prompts) return null;

  const update = (key: PromptKey, value: string) => {
    setDraft((current) => ({
      ...current,
      prompts: { ...current.prompts!, [key]: value },
    }));
  };

  const field = (key: PromptKey, label: string, help: string, extra?: React.ReactNode) => (
    <Box key={key} sx={{ mb: 2.5, '&:last-of-type': { mb: 0 } }}>
      <AdminLabeledControl label={label} hint={help}>
        {({ id, describedBy }) => (
          <TextField
            id={id}
            multiline
            minRows={3}
            fullWidth
            value={prompts[key] || ''}
            onChange={(event) => update(key, event.target.value)}
            inputProps={{ 'aria-describedby': describedBy }}
            sx={adminFieldSx()}
          />
        )}
      </AdminLabeledControl>
      {extra}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACE[5]}px`, maxWidth: 860 }}>
      <AdminSettingCard title={COPY.settingsPromptRewriteCard}>
        {REWRITE_FIELDS.map((item) => field(item.key, item.label, item.help))}
      </AdminSettingCard>
      <AdminSettingCard title={COPY.settingsPromptOtherCard}>
        {OTHER_FIELDS.map((item) => field(item.key, item.label, item.help))}
      </AdminSettingCard>
      <AdminSettingCard title={COPY.settingsPromptTechCard}>
        {field(
          'htmlRule',
          COPY.settingsPromptHtml,
          COPY.settingsPromptHtmlHelp,
          <Typography sx={{ mt: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
            {COPY.settingsPromptHtmlDiff}
          </Typography>,
        )}
        {field(
          'refineFit',
          COPY.settingsPromptRefine,
          COPY.settingsPromptRefineHelp,
          <Typography sx={{ mt: 1, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
            {COPY.settingsPromptRefineKeep}
          </Typography>,
        )}
      </AdminSettingCard>
    </Box>
  );
};

export default AdminPromptsSection;
